import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, Target, TrendingUp, PiggyBank, Check } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatRupiah, formatNumberInput, parseDigits } from "../lib/format";
import { StatCard } from "../components/Common";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli",
  "Agustus", "September", "Oktober", "November", "Desember"];

const monthLabel = (m) => {
  const [y, mo] = String(m).split("-");
  return `${MONTHS_ID[Number(mo) - 1] || ""} ${y}`;
};

function barColor(percent) {
  if (percent == null) return "bg-slate-600";
  if (percent > 100) return "bg-rose-500";
  if (percent >= 80) return "bg-amber-500";
  return "bg-emerald-500";
}

export default function Anggaran() {
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const canManage = perms.includes("manage_budget");
  const canResmi = perms.includes("view_saldo_resmi");
  const canImf = perms.includes("view_saldo_imf");

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [book, setBook] = useState(canResmi ? "resmi" : "imf");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingCat, setSavingCat] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/budgets?month=${month}&book=${book}`)
      .then((r) => { setData(r.data); setEdits({}); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [month, book]);

  useEffect(load, [load]);

  const saveRow = async (category) => {
    const raw = edits[category];
    const amount = parseDigits(raw);
    setSavingCat(category);
    try {
      await api.post("/budgets", { month, book, category, amount });
      toast.success(`Anggaran "${category}" disimpan`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingCat(null);
    }
  };

  const items = data?.items || [];
  const totalBudget = data?.total_budget || 0;
  const totalSpent = data?.total_spent || 0;
  const totalRemaining = data?.total_remaining || 0;
  const overallPct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : null;

  return (
    <div className="space-y-4" data-testid="anggaran-page">
      {/* Controls */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Bulan</label>
          <input
            type="month"
            data-testid="input-budget-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-lg border border-slate-700 bg-[#0F172A] px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400"
          />
        </div>
        {(canResmi && canImf) && (
          <div className="flex items-center gap-1 rounded-lg border border-slate-700 bg-[#0F172A] p-1" data-testid="budget-book-toggle">
            <button
              data-testid="btn-book-resmi"
              onClick={() => setBook("resmi")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${book === "resmi" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Kas Resmi
            </button>
            <button
              data-testid="btn-book-imf"
              onClick={() => setBook("imf")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${book === "imf" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"}`}
            >
              Kas IMF
            </button>
          </div>
        )}
        <div className="ml-auto text-xs text-slate-400">
          Anggaran <span className="font-semibold text-slate-200">{monthLabel(month)}</span> ·{" "}
          <span className={book === "imf" ? "text-emerald-400" : "text-cyan-400"}>
            {book === "imf" ? "Kas IMF" : "Kas Resmi"}
          </span>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard testid="budget-total" title="Total Anggaran" numeric={totalBudget} icon={Target} accent="blue" />
        <StatCard testid="budget-spent" title="Terpakai" numeric={totalSpent} icon={TrendingUp} accent="rose"
          sub={overallPct != null ? `${overallPct}% dari anggaran` : "Belum ada anggaran"} />
        <StatCard testid="budget-remaining" title="Sisa Anggaran" numeric={totalRemaining} icon={PiggyBank}
          accent={totalRemaining < 0 ? "rose" : "emerald"} />
      </div>

      {/* List */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-5 pt-5 pb-3">
          <div className="p-2 rounded-lg bg-blue-500/15 text-cyan-400"><Wallet className="w-4 h-4" /></div>
          <div>
            <h3 className="text-base font-bold text-slate-100">Anggaran per Kategori Pengeluaran</h3>
            <p className="text-xs text-slate-400">
              {canManage ? "Isi nominal anggaran, progress bar menunjukkan realisasi belanja bulan ini." : "Progress bar menunjukkan realisasi belanja terhadap anggaran."}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <p className="px-5 py-10 text-sm text-slate-400 text-center">Belum ada kategori pengeluaran.</p>
        ) : (
          <div className="divide-y divide-slate-800">
            {items.map((it) => {
              const pct = it.percent;
              const barPct = pct == null ? 0 : Math.min(100, pct);
              const over = pct != null && pct > 100;
              const edited = edits[it.category] !== undefined;
              const displayVal = edited ? edits[it.category] : (it.amount ? formatNumberInput(it.amount) : "");
              return (
                <div key={it.category} data-testid={`budget-row-${it.category}`} className="px-5 py-4 hover:bg-slate-800/30">
                  <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-100 truncate">{it.category}</p>
                      <p className="text-xs text-slate-400">
                        Terpakai <span className="font-num text-slate-200">{formatRupiah(it.spent)}</span>
                        {it.amount > 0 && <> dari <span className="font-num text-slate-200">{formatRupiah(it.amount)}</span></>}
                        {pct != null && (
                          <span className={`ml-2 font-semibold ${over ? "text-rose-400" : pct >= 80 ? "text-amber-400" : "text-emerald-400"}`}>
                            {pct}%
                          </span>
                        )}
                      </p>
                    </div>
                    {canManage ? (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">Rp</span>
                          <Input
                            data-testid={`input-budget-${it.category}`}
                            value={displayVal}
                            onChange={(e) => setEdits((s) => ({ ...s, [it.category]: formatNumberInput(parseDigits(e.target.value)) }))}
                            onKeyDown={(e) => e.key === "Enter" && edited && saveRow(it.category)}
                            placeholder="0"
                            className="w-36 pl-8 font-num text-right bg-[#0F172A] border-slate-700"
                          />
                        </div>
                        <Button
                          data-testid={`btn-save-budget-${it.category}`}
                          size="sm"
                          disabled={!edited || savingCat === it.category}
                          onClick={() => saveRow(it.category)}
                          className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white disabled:opacity-40"
                        >
                          {savingCat === it.category ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                      </div>
                    ) : (
                      <span className="font-num text-sm font-bold text-slate-200">
                        {it.amount > 0 ? formatRupiah(it.amount) : "Belum diatur"}
                      </span>
                    )}
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-slate-800 overflow-hidden">
                    <div
                      data-testid={`budget-bar-${it.category}`}
                      className={`h-full rounded-full transition-all duration-500 ${barColor(pct)}`}
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                  {over && (
                    <p className="text-[11px] text-rose-400 mt-1 font-medium">
                      Melebihi anggaran {formatRupiah(Math.abs(it.remaining))}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
